/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import SensorRequestMessage from '@/components/feedback/SensorRequestMessage';
import DashboardPanelHeader from '@/components/layout/DashboardPanelHeader';
import OverviewCardSummary from '@/components/tabs/overview/metrics/OverviewCardSummary';

/* Import libraries */
import { NamingConventions } from '@/lib/formatting/namingConventions';
import { getOverviewAnomalyBadgeClassName, getOverviewKpiAggregationLabel, getOverviewKpiSurfaceClassName, getOverviewStatusClass } from '@/lib/overview/overviewCards';
import type { OverviewAggregatedCardData } from '@/lib/overview/overviewSystemAggregation';
import { formatTimeSeriesNumericValue } from '@/lib/timeSeries/timeSeriesValue';

interface OverviewAggregatedCardProps {
    ariaLabel: string;
    card: OverviewAggregatedCardData;
    compact?: boolean;
    contentSized?: boolean;
    description: string;
    icon: LucideIcon;
    headerAction?: ReactNode;
    kpiGridClassName?: string;
    showKpis: boolean;
    summaryText?: string;
    title: string;
    to: string;
}

function getStatusLabel(status: number): string {
    if (status >= 2) {
        return 'Error';
    }

    if (status >= 1) {
        return 'Warning';
    }

    return 'OK';
}

function OverviewAggregatedCard({
    ariaLabel,
    card,
    compact = false,
    contentSized = false,
    description,
    headerAction,
    icon: Icon,
    kpiGridClassName = 'grid-cols-2',
    showKpis,
    summaryText,
    title,
    to,
}: OverviewAggregatedCardProps) {
    const { locale, t } = useI18n();

    return (
        <NavLink
            to={to}
            className={`dashboard-panel interactive-surface ${contentSized ? 'min-h-0 h-full self-stretch' : '!min-h-52 h-full'} items-start justify-start ${headerAction ? 'overflow-visible' : 'overflow-hidden'} p-0 text-left no-underline transition-[background,transform] duration-150 ${getOverviewStatusClass(card.status)}`}
            aria-label={ariaLabel}
        >
            <div className="flex h-full w-full flex-col">
                <DashboardPanelHeader
                    actions={headerAction || !compact ? (
                        <>
                                {headerAction}
                                {!compact ? card.anomalySensorCounts.map(({ anomaly, sensorCount }) => (
                                    <span
                                        key={anomaly}
                                        className={getOverviewAnomalyBadgeClassName(anomaly)}
                                    >
                                        {anomaly}: {sensorCount}
                                    </span>
                                )) : null}
                        </>
                    ) : undefined}
                    icon={Icon}
                    title={title}
                />

                <div className="flex flex-1 flex-col px-5 py-3">
                    {compact ? (
                        <>
                            <p className="text-sm leading-relaxed text-base-content/80">
                                {summaryText}
                            </p>

                            {card.errorMessages.length > 0 ? (
                                <div className="mt-4 rounded-box border border-error/30 bg-error/10 px-3 py-2 text-sm leading-relaxed text-error">
                                    <SensorRequestMessage message={card.errorMessages.join(' ')} />
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <p className="text-sm leading-relaxed text-base-content/80">
                                {description}
                            </p>
                            <OverviewCardSummary
                                sensorCount={card.sensorCount}
                                statusCounts={card.statusCounts}
                            />

                            {card.errorMessages.length > 0 ? (
                                <div className="mt-4 rounded-box border border-error/30 bg-error/10 px-3 py-2 text-sm leading-relaxed text-error">
                                    <SensorRequestMessage message={card.errorMessages.join(' ')} />
                                </div>
                            ) : null}
                        </>
                    )}

                    {showKpis ? (
                        <>
                            <div className="mt-4">
                                <div className="flex items-center justify-between gap-3 rounded-box bg-base-100/70 px-3 py-2">
                                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                    {t('Status')}
                                </span>
                                    <span className="text-sm font-semibold text-base-content">
                                    {t(getStatusLabel(card.status))}
                                </span>
                                </div>
                            </div>
                            <div className={`mt-2 grid gap-2 ${kpiGridClassName}`}>
                                {card.kpis.map((kpi) => (
                                    <div
                                        key={`${kpi.anomalyConfig.key}-${kpi.sensorType}`}
                                        className={`rounded-box px-3 py-2 ${getOverviewKpiSurfaceClassName(kpi.anomalyConfig.badgeLabel)}`}
                                        title={`${t(kpi.anomalyConfig.label)} (${kpi.anomalyConfig.badgeLabel}) - ${t('Aggregation')}: ${t(getOverviewKpiAggregationLabel(kpi.systemAggregation))}`}
                                    >
                                        <p className="mb-0 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                            {new NamingConventions(kpi.sensorType).getTitle()}
                                        </p>
                                        <p className="mb-0 mt-1 truncate text-sm font-semibold tabular-nums text-base-content">
                                            {kpi.value !== null
                                                ? `${formatTimeSeriesNumericValue(kpi.value, locale, kpi.fractionDigits)}${kpi.unit ? ` ${kpi.unit}` : ''}`
                                                : '-'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </NavLink>
    );
}

export default OverviewAggregatedCard;
